import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Trash, PencilSimple, Plus, Lightning, Heart, Warning, CookingPot, Star, SpinnerGap, ThumbsUp, ThumbsDown, ChartLineUp, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  preference: { icon: Heart, label: "Preferencia", color: "text-pink-600", bg: "bg-pink-50", border: "border-pink-200" },
  restriction: { icon: Warning, label: "Restricao", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  style: { icon: CookingPot, label: "Estilo", color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" },
  correction: { icon: Lightning, label: "Correcao", color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  pattern: { icon: Star, label: "Padrao", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
};

export default function ChefMemoryPanel() {
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [editText, setEditText] = useState("");
  const [editCategory, setEditCategory] = useState("preference");
  const [newText, setNewText] = useState("");
  const [newCategory, setNewCategory] = useState("preference");

  useEffect(() => { fetchMemory(); }, []);

  const fetchMemory = async () => {
    try {
      const { data } = await axios.get(`${API}/sous-chef/memory`);
      setMemory(data);
    } catch { toast.error("Erro ao carregar memoria"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (idx) => {
    try {
      await axios.delete(`${API}/sous-chef/memory/${idx}`);
      toast.success("Memoria removida");
      fetchMemory();
    } catch { toast.error("Erro ao remover"); }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Limpar TODA a memoria do Sous-Chef? Ele vai esquecer tudo que aprendeu sobre voce.")) return;
    try {
      await axios.delete(`${API}/sous-chef/memory`);
      toast.success("Memoria limpa");
      fetchMemory();
    } catch { toast.error("Erro ao limpar"); }
  };

  const handleAdd = async () => {
    if (!newText.trim()) return;
    try {
      await axios.post(`${API}/sous-chef/memory`, { text: newText, category: newCategory });
      toast.success("Memoria adicionada");
      setAddOpen(false);
      setNewText("");
      setNewCategory("preference");
      fetchMemory();
    } catch { toast.error("Erro ao adicionar"); }
  };

  const handleEdit = async () => {
    if (editIdx === null || !editText.trim()) return;
    try {
      await axios.put(`${API}/sous-chef/memory/${editIdx}`, { text: editText, category: editCategory });
      toast.success("Memoria atualizada");
      setEditIdx(null);
      fetchMemory();
    } catch { toast.error("Erro ao editar"); }
  };

  const startEdit = (idx, mem) => {
    setEditIdx(idx);
    setEditText(mem.text);
    setEditCategory(mem.category || "preference");
  };

  if (loading) return <div className="flex items-center justify-center h-64"><SpinnerGap size={32} className="animate-spin text-zinc-300" /></div>;

  const stats = memory?.stats || {};
  const memories = memory?.memories || [];
  const profile = memory?.profile || {};

  return (
    <div className="space-y-6" data-testid="chef-memory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
            <Brain size={24} weight="bold" className="text-purple-600" /> Memoria do Sous-Chef
          </h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">O que o Sous-Chef aprendeu sobre voce</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setAddOpen(true)} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="add-memory-btn">
            <Plus size={16} className="mr-1.5" /> Ensinar
          </Button>
          {memories.length > 0 && (
            <Button size="sm" variant="outline" onClick={handleClearAll} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-memory-btn">
              <Trash size={16} className="mr-1.5" /> Limpar Tudo
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Conversas", value: stats.total_chats || 0, icon: ChartLineUp, color: "text-zinc-600" },
          { label: "Memorias", value: memories.length, icon: Brain, color: "text-purple-600" },
          { label: "Feedbacks Positivos", value: stats.positive_feedback || 0, icon: ThumbsUp, color: "text-emerald-600" },
          { label: "Feedbacks Negativos", value: stats.negative_feedback || 0, icon: ThumbsDown, color: "text-rose-500" },
        ].map((s) => (
          <Card key={s.label} className="border-zinc-200">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-2">
                <s.icon size={16} className={s.color} weight="bold" />
                <span className="text-[10px] md:text-xs uppercase tracking-wider text-zinc-500 font-semibold">{s.label}</span>
              </div>
              <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-1">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Profile */}
      {profile.total_ingredients > 0 && (
        <Card className="border-zinc-200">
          <CardContent className="p-3 md:p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Perfil Gastronomico</p>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs bg-zinc-100 text-zinc-700 px-2 py-1 rounded">{profile.total_ingredients} ingredientes</span>
              <span className="text-xs bg-zinc-100 text-zinc-700 px-2 py-1 rounded">{profile.total_recipes} receitas</span>
              {(profile.top_ingredient_categories || []).map((cat) => (
                <span key={cat.name} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">{cat.name} ({cat.count})</span>
              ))}
            </div>
            {profile.recipe_names_sample?.length > 0 && (
              <p className="text-[10px] text-zinc-400 mt-2">Receitas: {profile.recipe_names_sample.join(", ")}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Memories */}
      {memories.length === 0 ? (
        <Card className="border-dashed border-zinc-300">
          <CardContent className="py-12 text-center">
            <Brain size={40} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500 font-medium">Nenhuma memoria ainda</p>
            <p className="text-xs text-zinc-400 mt-1">Use o Sous-Chef e de feedback — ele aprende automaticamente!</p>
            <p className="text-xs text-zinc-400 mt-0.5">Ou clique em &quot;Ensinar&quot; para adicionar algo manualmente.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">{memories.length} Aprendizado(s)</p>
          {memories.map((mem, idx) => {
            const config = CATEGORY_CONFIG[mem.category] || CATEGORY_CONFIG.pattern;
            const Icon = config.icon;
            const isEditing = editIdx === idx;

            return (
              <Card key={mem.id || `${mem.category}-${mem.text || idx}`} className={`${config.border} shadow-sm`} data-testid={`memory-${idx}`}>
                <CardContent className="p-3 flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Icon size={16} className={config.color} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="space-y-2">
                        <Input value={editText} onChange={(e) => setEditText(e.target.value)} className="text-sm" data-testid={`edit-memory-input-${idx}`} />
                        <div className="flex items-center gap-2">
                          <Select value={editCategory} onValueChange={setEditCategory}>
                            <SelectTrigger className="w-[140px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="sm" className="h-7 bg-emerald-600 text-white text-xs" onClick={handleEdit} data-testid={`save-edit-${idx}`}>
                            <Check size={12} className="mr-1" /> Salvar
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditIdx(null)}>
                            <X size={12} />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-zinc-800">{mem.text}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-semibold uppercase tracking-wider ${config.color}`}>{config.label}</span>
                          {mem.learned_at && <span className="text-[10px] text-zinc-400">{new Date(mem.learned_at).toLocaleDateString("pt-BR")}</span>}
                        </div>
                      </>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-700" onClick={() => startEdit(idx, mem)} data-testid={`edit-memory-${idx}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-red-500" onClick={() => handleDelete(idx)} data-testid={`delete-memory-${idx}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Memory Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Brain size={20} className="text-purple-600" /> Ensinar o Sous-Chef</DialogTitle>
            <DialogDescription>Adicione algo que o Sous-Chef deve lembrar sobre voce.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">O que ensinar</label>
              <Input value={newText} onChange={(e) => setNewText(e.target.value)}
                placeholder="Ex: Sempre uso manteiga sem sal nas receitas" className="mt-1" data-testid="new-memory-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Categoria</label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger className="mt-1" data-testid="new-memory-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2"><cfg.icon size={14} className={cfg.color} /> {cfg.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAdd} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="submit-memory-btn">
              <Brain size={16} className="mr-2" /> Ensinar ao Sous-Chef
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
